import { FunctionComponent, useState } from 'react';
import { Alert, Badge, Box, Button, Header, Modal, ProgressBar, SpaceBetween } from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { describeError } from 'common/utils/error-utils';
import { getBulkOrderQuantities, isBulkOrderEligibleForCompletion } from 'common/utils';
import { BulkOrderStatus } from 'configuration/config';
import { BulkOrdersService } from 'services/bulk-orders-service';

export interface CompleteBulkOrderModalProps {
    selectedItem: Order;
    showModal: boolean;
    /** shouldRefreshTable => this order completed; failed => backend error; abortQueue => X pressed, abandon the rest of the queue. */
    onDismiss: (shouldRefreshTable?: boolean, failed?: boolean, abortQueue?: boolean) => void;
    flashBarItemsActions: useFlashBarItemsActions;
    /** Shows "(current/total)" in the title; omit for a single-order flow. */
    progress?: { current: number; total: number };
}

export const CompleteBulkOrderModal: FunctionComponent<CompleteBulkOrderModalProps> = ({
    selectedItem, showModal, onDismiss, flashBarItemsActions, progress,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { received, requested } = getBulkOrderQuantities(selectedItem);
    const eligible = isBulkOrderEligibleForCompletion(selectedItem);
    const orderReference = selectedItem.orderReference || selectedItem.orderId;
    const productName =
        (selectedItem.items?.[0]?.itemInformation as any)?.name ||
        selectedItem.display ||
        getString('bulkOrders.submittedOrders.fallbackOrderName');

    const showSkip = (!!progress && progress.total > 1) || !eligible;

    /* istanbul ignore next */
    const skipOrder = () => onDismiss(false, false, false);
    /* istanbul ignore next */
    const abortQueue = () => onDismiss(false, false, true);

    const completeOrder = async () => {
        setIsSubmitting(true);
        try {
            await BulkOrdersService.instance.updateOrderStatus(selectedItem.orderId!, BulkOrderStatus.COMPLETED);
            flashBarItemsActions.addSuccessFlashBarItem(getString('bulkOrders.submittedOrders.orderCompleted', { orderReference }));
            onDismiss(true, false, false);
        } catch (error) {
            flashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
            onDismiss(false, true, false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const badges: JSX.Element[] = [];
    const realProductName = (selectedItem.items?.[0]?.itemInformation as any)?.name || selectedItem.display;
    if (realProductName) {
        badges.push(<Badge key="product" color="blue">{realProductName}</Badge>);
    }
    if (selectedItem.orderReference) {
        badges.push(<Badge key="ref" color="green">{getString('bulkOrders.submittedOrders.orderReferenceBadge', { orderReference: selectedItem.orderReference })}</Badge>);
    }

    return (
        <Modal
            data-testid="complete-bulk-order-modal"
            visible={showModal}
            onDismiss={abortQueue}
            closeAriaLabel={getString('bulkOrders.submittedOrders.closeAriaLabel')}
            header={
                <Header
                    variant="h2"
                    description={<SpaceBetween direction="horizontal" size="xs">{badges}</SpaceBetween>}
                >
                    {getString('bulkOrders.submittedOrders.completeModalTitle')}
                    {progress ? ` (${progress.current}/${progress.total})` : ''}
                </Header>
            }
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        {showSkip && (
                            <Button
                                data-testid="complete-bulk-order-skip"
                                variant="link"
                                onClick={skipOrder}
                                disabled={isSubmitting}
                            >
                                {getString('bulkOrders.submittedOrders.skipButton')}
                            </Button>
                        )}
                        <Button
                            data-testid="complete-bulk-order-confirm"
                            variant="primary"
                            loading={isSubmitting}
                            disabled={!eligible || isSubmitting}
                            onClick={completeOrder}
                        >
                            {getString('bulkOrders.submittedOrders.completeButton')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="s">
                {!eligible && (
                    <Alert type="warning" statusIconAriaLabel="Warning" data-testid="bulk-order-ineligible-alert">
                        {getString('bulkOrders.submittedOrders.ineligibleBanner', { received, requested })}
                    </Alert>
                )}
                <Box>
                    <b>{productName}</b>
                </Box>
                <ProgressBar
                    value={requested > 0 ? Math.min(Math.round((received / requested) * 100), 100) : 0}
                    additionalInfo={`${received} / ${requested} ${getString('bulkOrders.submittedOrders.seats')}`}
                    label={getString('bulkOrders.submittedOrders.receivedProgressLabel')}
                    {...(eligible ? { description: getString('bulkOrders.submittedOrders.fullyReceived') } : {})}
                />
            </SpaceBetween>
        </Modal>
    );
};
